import React, { useState, useCallback, useEffect, useRef } from "react"
import Typography from "aldoo-ra/Typography"
import Tabs from "aldoo-ra/Tabs"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import { PermissionsPlatformAPI } from "./permissions-api"
import APIKeysListTab from "./components/APIKeysListTab"
import APIKeyStatsTab from "./components/APIKeyStatsTab"
import {
  KeyIcon,
  ChartBarIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline"

/**
 * Main API Keys Manager Component
 * Organized with tabs similar to PackViewer
 */
const PlatformAPIKeys = () => {
  const [loading, setLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [availablePermissions, setAvailablePermissions] = useState([])
  const [activeTab, setActiveTab] = useState("keys")
  const apiKeysListRef = useRef(null)

  // Global refresh function
  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1)
  }, [])

  // Global error handler
  const handleError = useCallback((errorMessage) => {
    showNotification({
      message: errorMessage,
      className: "bg-admin_error text-white",
      duration: 4000
    })
  }, [])

  // Load available permissions on mount
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const permissions = await PermissionsPlatformAPI.GetAvailablePermissions()
        setAvailablePermissions(permissions)
      } catch (error) {
        console.error("Failed to load available permissions:", error)
      }
    }
    loadPermissions()
  }, [])

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 border-b border-admin_bordered bg-admin_paper p-6">
        {/* Left side: Title and description */}
        <div className="flex items-start">
          <div>
            <Typography 
              component="h1"
              variant="admin_h3"
            >
              API Keys Management
            </Typography>

            <Typography variant="admin_txt1_dim">
              Manage API keys for programmatic access to platform resources
            </Typography>
          </div>
        </div>

        {/* Right side: Action buttons */}
        <Button
          variant="admin_primary"
          className="flex items-center"
          onClick={() => {
            // Switch to keys tab and trigger create modal
            setActiveTab("keys")
            if (apiKeysListRef.current) {
              apiKeysListRef.current.openCreateModal()
            }
          }}
        >
          <PlusCircleIcon className="w-5 h-5 mr-1" />
          Create API Key
        </Button>
        
      </div>

      {/* Content Tabs */}
      <div className="flex-1 p-6">
        <Tabs
          variant="admin"
          value={activeTab}
          onChange={setActiveTab}
          tabs={[
            { 
              value: "keys", 
              label: (
                <div className="flex items-center space-x-2">
                  <KeyIcon className="w-4 h-4" />
                  <span>API Keys</span>
                </div>
              )
            },
            { 
              value: "statistics", 
              label: (
                <div className="flex items-center space-x-2">
                  <ChartBarIcon className="w-4 h-4" />
                  <span>Statistics</span>
                </div>
              )
            },
          ]}
          className="mb-6"
        />

        {/* Tab Content */}
        {activeTab === "keys" && (
          <div className="bg-admin_paper rounded-xl p-6">
            <APIKeysListTab
              ref={apiKeysListRef}
              refreshKey={refreshKey}
              onRefresh={handleRefresh}
              onError={handleError}
              loading={loading}
              setLoading={setLoading}
              availablePermissions={availablePermissions}
            />
          </div>
        )}

        {activeTab === "statistics" && (
          <div>
            <APIKeyStatsTab
              refreshKey={refreshKey}
              onRefresh={handleRefresh}
              onError={handleError}
              loading={loading}
              setLoading={setLoading}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default PlatformAPIKeys